import { solve } from './solver';

// A new player's first few boards start with some queens already on them.
//
// The first Queens puzzle anyone sees is a blank grid of colored blobs with no obvious opening,
// and the whole game is "find the first queen". Handing over a few of them turns that opening
// into the part of the puzzle a beginner can actually do — the deductions that follow from a
// queen already placed — and the help tapers off (3 → 2 → 1 → none) rather than stopping dead.
//
// Reveals are counted as an assist: a run played with them is tagged 'New Player' on the
// leaderboard and ranks below every other class for wins (see assistClass in
// src/shared/analysis/record.ts). It is a teaching aid, not a shortcut to the top of a board.

/** How many puzzles get help, and how much: the nth solve-free puzzle gets REVEALS[n] queens. */
const REVEALS = [3, 2, 1];

/**
 * Queens revealed on a player's `solveCount`-th puzzle (0-based — their very first is 0).
 *
 * Keyed on *solves*, not on puzzles opened: a beginner who opens five boards before finishing
 * one is still on their first puzzle in every sense that matters here. The count passed in must
 * exclude the puzzle being played, so revisiting an early puzzle shows the same board it did the
 * first time rather than one that has quietly got harder.
 */
export function revealCountFor(solveCount: number): number {
  return REVEALS[solveCount] ?? 0;
}

/**
 * Pick `n` queens from a solution to show up front.
 *
 * Deterministic in the puzzle id, so a reload — or a second visit — reveals the same queens and
 * the player never sees the board shift under them. Spread over the board rather than taken in
 * row order: three queens in the top three rows leave the bottom of the grid as blank as it
 * started, which is the half a beginner is stuck on.
 *
 * Returns cell keys ("r,c"), or an empty list if the puzzle has no solution to draw from.
 */
export function pickRevealedQueens(colorRegions: string[][], n: number, seed: number): string[] {
  if (n <= 0) return [];
  const [solution] = solve(colorRegions, 1);
  if (!solution) return [];

  // One queen per row, so the solution is already a list of `size` candidates: shuffle it with a
  // seeded LCG and take the first n.
  const rows = solution.map((col, row) => ({ row, col }));
  let s = (seed * 2654435761) % 2147483647 || 1;
  const rand = () => (s = (s * 48271) % 2147483647) / 2147483647;
  for (let i = rows.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [rows[i], rows[j]] = [rows[j], rows[i]];
  }
  return rows.slice(0, Math.min(n, rows.length)).map(q => `${q.row},${q.col}`);
}
